/**
 * The firm's material library (0039): the decisions, kept out of the views.
 *
 * COPY, DON'T LINK: adding a library item to a room makes the room its own
 * material — see the header of 0039 for why a live link would let one price
 * change rewrite other projects' budgets.
 */

import type { MaterialLibraryRow, SpecRow } from '@/types/database';

type LibraryFields = Pick<MaterialLibraryRow,
  'item' | 'vendor' | 'sku' | 'unit' | 'unit_cost_cents' | 'material_category_id'
  | 'budget_category_id' | 'source_url' | 'notes'>;

function norm(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Items matching every word of `query` across item, vendor, SKU and notes,
 * in an optional material category. Alphabetical by item.
 */
export function searchLibrary<T extends Pick<MaterialLibraryRow,
  'item' | 'vendor' | 'sku' | 'notes' | 'material_category_id'>>(
  items: readonly T[], query: string, categoryId: string | null = null,
): T[] {
  const words = norm(query).split(' ').filter(Boolean);
  return items
    .filter((i) => !categoryId || i.material_category_id === categoryId)
    .filter((i) => {
      const hay = norm([i.item, i.vendor, i.sku, i.notes].join(' '));
      return words.every((w) => hay.includes(w));
    })
    .slice()
    .sort((a, b) => norm(a.item).localeCompare(norm(b.item)));
}

/**
 * The new material a library item becomes in a room. The photo is NOT
 * carried here: it lives in the library's bucket and has to be COPIED into
 * the project's own folder first (the client portal only signs files there).
 */
export function specFromLibrary(
  lib: LibraryFields & Pick<MaterialLibraryRow, 'id'>,
  opts: { roomId: string; quantity: number; sortOrder: number },
): {
  room_id: string; item: string; vendor: string | null; sku: string | null; quantity: number;
  unit: string | null; unit_cost_cents: number | null; material_category_id: string | null;
  budget_category_id: string | null; source_url: string | null; notes: string | null;
  status: 'proposed'; library_item_id: string; sort_order: number;
} {
  return {
    room_id: opts.roomId,
    item: lib.item,
    vendor: lib.vendor,
    sku: lib.sku,
    quantity: opts.quantity > 0 ? opts.quantity : 1,
    unit: lib.unit,
    // The reference price is where the project's price STARTS; the designer
    // adjusts it in the project without touching the library.
    unit_cost_cents: lib.unit_cost_cents,
    material_category_id: lib.material_category_id,
    budget_category_id: lib.budget_category_id,
    source_url: lib.source_url,
    notes: lib.notes,
    // A new selection in a room is proposed, whatever it was elsewhere.
    status: 'proposed',
    library_item_id: lib.id,
    sort_order: opts.sortOrder,
  };
}

/**
 * The library item a project's material becomes when saved to the library.
 * Only what describes the PRODUCT travels — never quantity, status or dates,
 * which belong to that one project.
 */
export function libraryFromSpec(
  s: Pick<SpecRow, 'item' | 'vendor' | 'sku' | 'unit' | 'unit_cost_cents' | 'material_category_id'
    | 'budget_category_id' | 'source_url' | 'notes'>,
): LibraryFields {
  return {
    item: s.item.trim(),
    vendor: s.vendor?.trim() || null,
    sku: s.sku?.trim() || null,
    unit: s.unit?.trim() || null,
    unit_cost_cents: s.unit_cost_cents,
    material_category_id: s.material_category_id,
    budget_category_id: s.budget_category_id,
    source_url: s.source_url?.trim() || null,
    notes: s.notes?.trim() || null,
  };
}

/**
 * An existing library item that looks like the same product, or null. Same
 * vendor + SKU when both have a SKU; otherwise same name from the same vendor.
 * A warning, never a block — two finishes of one product can share a name.
 */
export function findLikelyDuplicate<T extends Pick<MaterialLibraryRow, 'item' | 'vendor' | 'sku'>>(
  items: readonly T[], candidate: Pick<MaterialLibraryRow, 'item' | 'vendor' | 'sku'>,
): T | null {
  const v = norm(candidate.vendor);
  const sku = norm(candidate.sku);
  const name = norm(candidate.item);
  return items.find((i) => {
    if (sku && norm(i.sku)) return norm(i.sku) === sku && norm(i.vendor) === v;
    return norm(i.item) === name && norm(i.vendor) === v;
  }) ?? null;
}

/** Library photo path: `<firm_id>/<uuid>-<safe name>` (0039's storage rule). */
export function libraryPhotoPath(firmId: string, fileName: string, uuid: string): string {
  const safe = fileName.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80) || 'photo';
  return `${firmId}/${uuid}-${safe}`;
}

/** The file-name part of a storage path, for naming a copy. */
export function fileNameOf(path: string): string {
  const last = path.split('/').pop() ?? 'photo';
  // Drop a leading "<uuid>-" so copies don't stack uuids.
  return last.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '') || 'photo';
}
